/**
 * Taxonomy editor (F9).
 *
 * Pick a vocabulary, see its terms with a usage count each, rename a term,
 * add one, or delete an unused non-system one. The usage count is what makes
 * deletion safe to offer: a term in use has its delete button disabled with the
 * count as the reason, so the destructive action is unavailable rather than
 * offered-then-refused.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.13, PRD §5.1, F9
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TaxonomyKind, TaxonomyTerm } from '@nexus/contracts';

import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Field } from '../../../components/ui/Field';
import { Input } from '../../../components/ui/Input';
import { Kicker } from '../../../components/ui/Kicker';
import { Select } from '../../../components/ui/Select';
import { ApiError } from '../../../lib/problem';
import {
  useCreateTaxonomy,
  useDeleteTaxonomy,
  useTaxonomy,
  useUpdateTaxonomy,
} from '../api/admin.queries';

/** The vocabularies, in the order the picker lists them. */
const KINDS: readonly TaxonomyKind[] = [
  'TECHNOLOGY_TAG',
  'SECTOR',
  'DECARBONISATION_DRIVER',
  'REJECTION_REASON',
  'FUNDING_SOURCE',
  'SOURCE_CHANNEL',
  'USE_CASE_CATEGORY',
];

export function TaxonomyManager(): React.JSX.Element {
  const { t } = useTranslation();
  const [kind, setKind] = useState<TaxonomyKind>('TECHNOLOGY_TAG');
  const { data, isPending, isError } = useTaxonomy({ kind, includeInactive: true });

  return (
    <Card>
      <CardHead
        actions={
          <Select
            value={kind}
            onValueChange={(value) => {
              setKind(value as TaxonomyKind);
            }}
            options={KINDS.map((k) => ({
              value: k,
              label: t(`admin.taxonomyKind.${k}`),
            }))}
            ariaLabel={t('admin.taxonomy.pickKind')}
            className="max-w-[240px]"
          />
        }
      >
        <Kicker as="h2">{t('admin.taxonomy.title')}</Kicker>
      </CardHead>
      <CardBody>
        {isError ? (
          <p role="alert" className="text-sem-bad">
            {t('errors.generic')}
          </p>
        ) : isPending ? (
          <p className="text-body" aria-busy="true">
            {t('state.loading')}
          </p>
        ) : (
          <>
            <TermList kind={kind} terms={data} />
            <NewTermForm kind={kind} />
          </>
        )}
      </CardBody>
    </Card>
  );
}

function TermList({
  kind,
  terms,
}: {
  kind: TaxonomyKind;
  terms: readonly TaxonomyTerm[];
}): React.JSX.Element {
  const { t } = useTranslation();

  if (terms.length === 0) {
    return (
      <p className="text-body mb-s3 text-sm font-light">{t('admin.taxonomy.empty')}</p>
    );
  }

  return (
    <ul className="mb-s3 flex flex-col divide-y divide-gray-100">
      {terms.map((term) => (
        <TermRow key={term.id} kind={kind} term={term} />
      ))}
    </ul>
  );
}

function TermRow({
  kind,
  term,
}: {
  kind: TaxonomyKind;
  term: TaxonomyTerm;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [label, setLabel] = useState(term.label);
  const [seededLabel, setSeededLabel] = useState(`${term.id}:${String(term.version)}`);
  const rowKey = `${term.id}:${String(term.version)}`;
  if (seededLabel !== rowKey) {
    setSeededLabel(rowKey);
    setLabel(term.label);
  }

  const update = useUpdateTaxonomy(term.id);
  const remove = useDeleteTaxonomy();

  const dirty = label.trim() !== term.label && label.trim() !== '';
  const inUse = term.usageCount > 0;

  return (
    <li className="flex flex-wrap items-center gap-2 py-2.5">
      <input
        aria-label={t('admin.taxonomy.labelFor', { key: term.key })}
        value={label}
        onChange={(event) => {
          setLabel(event.target.value);
        }}
        className="border-gray-200 text-navy-text min-h-10 flex-1 rounded-md border bg-white px-3 text-sm"
      />
      <span className="text-gray-400 font-display text-[11px] tracking-wide tabular-nums">
        {t('admin.taxonomy.usage', { count: term.usageCount })}
      </span>
      {!term.isActive && (
        <span className="text-body text-[11px] uppercase">
          {t('admin.taxonomy.inactive')}
        </span>
      )}
      <Button
        variant="secondary"
        size="sm"
        disabled={!dirty || update.isPending}
        onClick={() => {
          update.mutate({ version: term.version, label: label.trim() });
        }}
      >
        {t('actions.save')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        /* A system term or one in use cannot be deleted — the button is
           disabled with the reason, rather than offered and then refused. */
        disabled={term.isSystem || inUse || remove.isPending}
        title={
          term.isSystem
            ? t('admin.taxonomy.systemLocked')
            : inUse
              ? t('admin.taxonomy.inUseLocked', { count: term.usageCount })
              : undefined
        }
        onClick={() => {
          remove.mutate(term.id);
        }}
      >
        {t('actions.delete')}
      </Button>
      {update.isError && (
        <span role="alert" className="text-sem-bad w-full text-xs">
          {messageFor(update.error, t('errors.generic'))}
        </span>
      )}
      {remove.isError && (
        <span role="alert" className="text-sem-bad w-full text-xs">
          {messageFor(remove.error, t('errors.generic'))}
        </span>
      )}
      <span className="sr-only">{kind}</span>
    </li>
  );
}

function NewTermForm({ kind }: { kind: TaxonomyKind }): React.JSX.Element {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const create = useCreateTaxonomy();

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    create.mutate(
      { kind, key: key.trim(), label: label.trim() },
      {
        onSuccess: () => {
          setKey('');
          setLabel('');
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="border-gray-100 flex flex-wrap items-end gap-2 border-t pt-s3"
    >
      <Field label={t('admin.taxonomy.newKey')} className="flex-1">
        {({ id, ...described }) => (
          <Input
            id={id}
            value={key}
            placeholder="solar-pv"
            {...described}
            onChange={(event) => {
              setKey(event.target.value);
            }}
          />
        )}
      </Field>
      <Field label={t('admin.taxonomy.newLabel')} className="flex-1">
        {({ id, ...described }) => (
          <Input
            id={id}
            value={label}
            placeholder="Solar PV"
            {...described}
            onChange={(event) => {
              setLabel(event.target.value);
            }}
          />
        )}
      </Field>
      <Button
        type="submit"
        variant="action"
        disabled={key.trim() === '' || label.trim() === '' || create.isPending}
      >
        {t('admin.taxonomy.add')}
      </Button>
      {create.isError && (
        <span role="alert" className="text-sem-bad w-full text-xs">
          {messageFor(create.error, t('errors.generic'))}
        </span>
      )}
    </form>
  );
}

/** The API's message when there is one, else a generic fallback. */
function messageFor(error: Error, fallback: string): string {
  return error instanceof ApiError ? (error.problem.detail ?? fallback) : fallback;
}
